// Custom hook for question builder functionality
import { useState, useCallback } from "react";
import {
  Question,
  QuestionType,
  QuestionBuilderState,
} from "../types/question";

const getDefaultQuestionData = (type: QuestionType): any => {
  switch (type) {
    case "multiple_choice":
      return {
        options: ["", "", "", ""],
        correctAnswer: "",
        shuffleOptions: false,
      };
    case "multiple_select":
      return {
        options: ["", "", "", ""],
        correctAnswers: [],
        shuffleOptions: false,
      };
    case "true_false":
      return {
        correctAnswer: "True",
      };
    case "short_answer":
      return {
        correctAnswers: [""],
        caseSensitive: false,
        exactMatch: false,
      };
    case "essay":
      return {
        minWords: 0,
        maxWords: 0,
        rubric: [],
      };
    case "fill_blank":
      return {
        questionText: "",
        blanks: [],
      };
    case "matching":
      return {
        leftColumn: [],
        rightColumn: [],
        correctMatches: [],
      };
    case "ordering":
      return {
        items: [],
      };
    case "numeric":
      return {
        correctAnswer: 0,
        tolerance: 0,
        unit: "",
      };
    case "comprehension":
      return {
        passage: "",
        subQuestions: [
          {
            _id: `temp_${Date.now()}_1`,
            type: "multiple_choice",
            question: "",
            options: ["", "", "", ""],
            correctAnswer: "",
            points: 1,
          },
        ],
      };
    default:
      return {};
  }
};

export const useQuestionBuilder = () => {
  const [currentQuestion, setCurrentQuestion] = useState<QuestionBuilderState>({
    type: "multiple_choice",
    question: "",
    points: 1,
    difficulty: "medium",
    explanation: "",
    tags: [],
    estimatedTime: 60,
    typeSpecificData: getDefaultQuestionData("multiple_choice"),
  });

  const [questions, setQuestions] = useState<Question[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const updateQuestion = useCallback(
    (field: keyof QuestionBuilderState, value: any) => {
      setCurrentQuestion((prev) => ({
        ...prev,
        [field]: value,
      }));

      // Clear error for this field
      if (errors[field]) {
        setErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors[field];
          return newErrors;
        });
      }
    },
    [errors]
  );

  const updateTypeSpecificData = useCallback((field: string, value: any) => {
    setCurrentQuestion((prev) => ({
      ...prev,
      typeSpecificData: {
        ...prev.typeSpecificData,
        [field]: value,
      },
    }));
  }, []);

  const changeQuestionType = useCallback((type: QuestionType) => {
    setCurrentQuestion((prev) => ({
      ...prev,
      type,
      typeSpecificData: getDefaultQuestionData(type),
    }));
  }, []);

  const validateQuestion = useCallback((): boolean => {
    const newErrors: Record<string, string> = {};

    // Basic validation - question text is required for most types, but optional for comprehension
    if (
      currentQuestion.type !== "comprehension" &&
      !currentQuestion.question.trim()
    ) {
      newErrors.question = "Question text is required";
    }

    // Points validation - skip for comprehension as points are calculated from subQuestions
    if (
      currentQuestion.type !== "comprehension" &&
      currentQuestion.points <= 0
    ) {
      newErrors.points = "Points must be greater than 0";
    }

    // Type-specific validation
    const { type, typeSpecificData } = currentQuestion;

    switch (type) {
      case "multiple_choice":
        if (!typeSpecificData.correctAnswer) {
          newErrors.correctAnswer = "Correct answer is required";
        }
        if (
          typeSpecificData.options.filter((opt: string) => opt.trim()).length <
          2
        ) {
          newErrors.options = "At least 2 options are required";
        }
        break;

      case "multiple_select":
        if (
          !typeSpecificData.correctAnswers ||
          typeSpecificData.correctAnswers.length === 0
        ) {
          newErrors.correctAnswers = "At least one correct answer is required";
        }
        if (
          typeSpecificData.options.filter((opt: string) => opt.trim()).length <
          2
        ) {
          newErrors.options = "At least 2 options are required";
        }
        break;

      case "short_answer":
        if (
          !typeSpecificData.correctAnswers ||
          typeSpecificData.correctAnswers.length === 0 ||
          !typeSpecificData.correctAnswers[0].trim()
        ) {
          newErrors.correctAnswers = "At least one correct answer is required";
        }
        break;

      case "fill_blank":
        if (
          !typeSpecificData.questionText ||
          !typeSpecificData.questionText.includes("___")
        ) {
          newErrors.questionText =
            "Question must contain at least one blank (use ___)";
        }
        break;

      case "matching":
        if (
          typeSpecificData.leftColumn.length < 2 ||
          typeSpecificData.rightColumn.length < 2
        ) {
          newErrors.columns = "At least 2 items in each column are required";
        }
        break;

      case "ordering":
        if (typeSpecificData.items.length < 2) {
          newErrors.items = "At least 2 items to order are required";
        }
        break;

      case "numeric":
        if (typeof typeSpecificData.correctAnswer !== "number") {
          newErrors.correctAnswer = "Valid numeric answer is required";
        }
        break;

      case "comprehension":
        if (!typeSpecificData.passage || !typeSpecificData.passage.trim()) {
          newErrors.passage = "Passage text is required";
        }
        if (
          !typeSpecificData.subQuestions ||
          typeSpecificData.subQuestions.length === 0
        ) {
          newErrors.subQuestions = "At least one sub-question is required";
        } else {
          // Validate each sub-question
          typeSpecificData.subQuestions.forEach((subQ: any, index: number) => {
            if (!subQ.question || !subQ.question.trim()) {
              newErrors[`subQuestion_${index}_question`] = `Sub-question ${
                index + 1
              } text is required`;
            }
            if (subQ.points <= 0) {
              newErrors[`subQuestion_${index}_points`] = `Sub-question ${
                index + 1
              } points must be greater than 0`;
            }
            // Validate correct answer based on sub-question type
            if (subQ.type === "multiple_choice" || subQ.type === "true_false") {
              if (!subQ.correctAnswer || !subQ.correctAnswer.trim()) {
                newErrors[
                  `subQuestion_${index}_correctAnswer`
                ] = `Sub-question ${index + 1} correct answer is required`;
              }
            } else if (
              subQ.type === "short_answer" ||
              subQ.type === "fill_blank"
            ) {
              if (
                !subQ.correctAnswers ||
                subQ.correctAnswers.length === 0 ||
                !subQ.correctAnswers[0]?.trim()
              ) {
                newErrors[
                  `subQuestion_${index}_correctAnswers`
                ] = `Sub-question ${index + 1} correct answer is required`;
              }
            }
            // Essay questions don't need correct answers validation
          });
        }
        break;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [currentQuestion]);

  const buildQuestion = useCallback((): Question | null => {
    if (!validateQuestion()) return null;

    const baseQuestion = {
      type: currentQuestion.type,
      question:
        currentQuestion.question ||
        (currentQuestion.type === "comprehension"
          ? "Read the passage below and answer the questions."
          : ""),
      points:
        currentQuestion.type === "comprehension"
          ? currentQuestion.typeSpecificData.subQuestions?.reduce(
              (sum: number, subQ: any) => sum + (subQ.points || 0),
              0
            ) || 1
          : currentQuestion.points,
      difficulty: currentQuestion.difficulty,
      explanation: currentQuestion.explanation,
      tags: currentQuestion.tags,
      estimatedTime: currentQuestion.estimatedTime,
    };

    // For comprehension questions, explicitly include passage and subQuestions
    if (currentQuestion.type === "comprehension") {
      const question: Question = {
        ...baseQuestion,
        passage: currentQuestion.typeSpecificData.passage || "",
        subQuestions: (currentQuestion.typeSpecificData.subQuestions || []).map(
          (subQ: any) => {
            // Remove temp _id fields to let Mongoose generate proper ObjectIds
            const { _id, ...subQWithoutId } = subQ;
            return subQWithoutId;
          }
        ),
      } as Question;
      return question;
    }

    // For other question types, use the spread
    const question: Question = {
      ...baseQuestion,
      ...currentQuestion.typeSpecificData,
    } as Question;

    return question;
  }, [currentQuestion, validateQuestion]);

  const addQuestion = useCallback((): boolean => {
    const question = buildQuestion();
    if (!question) return false;

    setQuestions((prev) => [...prev, question]);

    // Reset current question
    setCurrentQuestion({
      type: "multiple_choice",
      question: "",
      points: 1,
      difficulty: "medium",
      explanation: "",
      tags: [],
      estimatedTime: 60,
      typeSpecificData: getDefaultQuestionData("multiple_choice"),
    });

    setErrors({});
    return true;
  }, [buildQuestion]);

  const updateQuestionAtIndex = useCallback(
    (index: number, question: Question) => {
      setQuestions((prev) => {
        const newQuestions = [...prev];
        newQuestions[index] = question;
        return newQuestions;
      });
    },
    []
  );

  const removeQuestion = useCallback((index: number) => {
    setQuestions((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const moveQuestion = useCallback((fromIndex: number, toIndex: number) => {
    setQuestions((prev) => {
      const newQuestions = [...prev];
      const [movedQuestion] = newQuestions.splice(fromIndex, 1);
      newQuestions.splice(toIndex, 0, movedQuestion);
      return newQuestions;
    });
  }, []);

  const resetBuilder = useCallback(() => {
    setCurrentQuestion({
      type: "multiple_choice",
      question: "",
      points: 1,
      difficulty: "medium",
      explanation: "",
      tags: [],
      estimatedTime: 60,
      typeSpecificData: getDefaultQuestionData("multiple_choice"),
    });
    setQuestions([]);
    setErrors({});
  }, []);

  const loadQuestions = useCallback((questionsToLoad: Question[]) => {
    setQuestions(questionsToLoad);
  }, []);

  const getTotalPoints = useCallback(() => {
    return questions.reduce((total, q) => total + q.points, 0);
  }, [questions]);

  const getEstimatedTime = useCallback(() => {
    return questions.reduce((total, q) => total + (q.estimatedTime || 60), 0);
  }, [questions]);

  const clearError = useCallback((field: string) => {
    setErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[field];
      return newErrors;
    });
  }, []);

  return {
    currentQuestion,
    questions,
    errors,
    updateQuestion,
    updateTypeSpecificData,
    changeQuestionType,
    validateQuestion,
    buildQuestion,
    addQuestion,
    updateQuestionAtIndex,
    removeQuestion,
    moveQuestion,
    resetBuilder,
    loadQuestions,
    getTotalPoints,
    getEstimatedTime,
    clearError,
  };
};

export default useQuestionBuilder;
